import { FC, useEffect, useMemo, useState } from 'react';
import { ActorType } from 'features/new-actor/enums/actorType';
import { Form, Formik } from 'formik';
import { GenericLoading } from 'components/ui/Loader';
import { BorrowerInsuredFormValues } from './borrowerInsuredForm';
import { useBorrowerInsuredForm } from './useBorrowerInsuredForm';
import {
  useBorrowerActorType,
  BorrowerActorType,
  BorrowerFormInitialData
} from './useBorrowerActorType';
import { borrowerNaturalFormConfig } from './natural/borrowerNaturalFormConfig';
import { borrowerLegalFormConfig } from './legal/borrowerLegalFormConfig';
import { BorrowerActorSelector } from './shared/BorrowerActorSelector';
import { BorrowerNaturalLayout } from './natural/BorrowerNaturalLayout';
import { BorrowerLegalLayout } from './legal/BorrowerLegalLayout';

interface BorrowerInsuredFormProps {
  reload: () => void;
  personId?: string;
  excludeIds?: string[];
  initialTab?: number | string;
  /** Id du projet fourni par l'ouvrant (prioritaire sur l'URL) — fiable en panneau. */
  borrowerProjectId?: string;
}

const ValidationTracker = ({
  isValid,
  onValidationChange
}: {
  isValid: boolean;
  onValidationChange: (isValid: boolean) => void;
}) => {
  const [lastValidState, setLastValidState] = useState(isValid);
  useEffect(() => {
    if (isValid !== lastValidState) {
      setLastValidState(isValid);
      onValidationChange(isValid);
    }
  }, [isValid, lastValidState, onValidationChange]);
  return null;
};

export const BorrowerInsuredForm: FC<BorrowerInsuredFormProps> = ({
  personId,
  reload,
  excludeIds = [],
  initialTab,
  borrowerProjectId
}) => {
  const {
    pendingBorrower,
    handleSubmit,
    editConfig,
    formikRef,
    handleValidationChange,
    isLoading
  } = useBorrowerInsuredForm(reload, personId, borrowerProjectId);

  const formConfig = useMemo(
    () => ({
      [ActorType.BorrowerNatural]: borrowerNaturalFormConfig,
      [ActorType.BorrowerLegal]: borrowerLegalFormConfig
    }),
    []
  );

  const isEdit = useMemo(() => !!editConfig, [editConfig]);

  const { actorType, handleActorTypeChange, currentFormConfig, savedValues } =
    useBorrowerActorType(
      formConfig,
      editConfig?.initialValues as BorrowerFormInitialData | undefined
    );

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Create a unique key for Formik based on actorType and pendingBorrower
  const formKey = useMemo(() => {
    if (pendingBorrower?.id) return `${actorType}-${pendingBorrower.id}`;
    return `${actorType}-new`;
  }, [actorType, pendingBorrower?.id]);

  useEffect(() => {
    if (isInitialLoadComplete) {
      handleValidationChange(true);
    }
  }, [isInitialLoadComplete, handleValidationChange]);

  useEffect(() => {
    if (savedValues[actorType] && !isInitialLoadComplete) {
      setTimeout(() => {
        setIsInitialLoadComplete(true);
      }, 0);
    }
  }, [savedValues, actorType, isInitialLoadComplete]);

  const handleFormSubmit = (values: BorrowerInsuredFormValues) => {
    const data = editConfig && editConfig.initialData;
    if (editConfig?.onSubmit && data) {
      editConfig.onSubmit(values, data, actorType);
    } else {
      handleSubmit(values, actorType);
    }
  };

  if (isLoading) {
    return <GenericLoading loading isParentSized />;
  }

  if (personId && !pendingBorrower) {
    return <GenericLoading loading isParentSized />;
  }

  return (
    <Formik
      key={formKey}
      enableReinitialize
      initialValues={savedValues[actorType] as BorrowerInsuredFormValues}
      validationSchema={currentFormConfig.validationSchema}
      validateOnMount={isInitialLoadComplete}
      onSubmit={handleFormSubmit}
      innerRef={formikRef}
    >
      {({ values, setValues, isValid }) => (
        <>
          <ValidationTracker
            isValid={isValid}
            onValidationChange={handleValidationChange}
          />
          <Form>
            <div>
              <div className="flex items-center mb-4">
                <BorrowerActorSelector
                  actorType={actorType}
                  isEdit={isEdit}
                  excludeIds={excludeIds}
                  onChange={(type: ActorType) =>
                    handleActorTypeChange(
                      type as BorrowerActorType,
                      values,
                      setValues
                    )
                  }
                />
              </div>
              {actorType === ActorType.BorrowerNatural ? (
                <BorrowerNaturalLayout isEdit={isEdit} initialTab={initialTab} />
              ) : (
                <BorrowerLegalLayout isEdit={isEdit} initialTab={initialTab} />
              )}
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};
